import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  createVacationAsync,
  updateVacationAsync,
  fetchVacationsAsync,
  fetchVacationTypesAsync,
  clearError,
} from '../store/slices/vacationsSlice';
import LoadingSpinner from './LoadingSpinner';
import StudentVacationPeriodPicker from './StudentVacationPeriodPicker';
import type { VacationPeriodChange } from './StudentVacationPeriodPicker';
import './GroupForm.css';

const VacationForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const { vacations, types, loading, error } = useAppSelector((state) => state.vacations);

  const [formData, setFormData] = useState({
    vacationTypeId: '',
    dateFrom: '',
    dateTo: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load types on mount
  useEffect(() => {
    if (types.length === 0) {
      dispatch(fetchVacationTypesAsync());
    }
  }, [dispatch, types.length]);

  // Load vacations if editing and not yet loaded
  useEffect(() => {
    if (isEditing && vacations.length === 0) {
      dispatch(fetchVacationsAsync());
    }
  }, [dispatch, isEditing, vacations.length]);

  // Set default type when types load (for create mode). Adjust state during
  // render (the React "you might not need an effect" pattern) so the default
  // lands before paint without a cascading effect. Track the last seen type
  // count so we only apply the default on the transition to having types.
  const [seenTypesCount, setSeenTypesCount] = useState(types.length);
  if (types.length !== seenTypesCount) {
    setSeenTypesCount(types.length);
    if (!isEditing && types.length > 0 && !formData.vacationTypeId) {
      setFormData((prev) => ({ ...prev, vacationTypeId: types[0].id.toString() }));
    }
  }

  // Populate form when editing. Adjust state during render when the target
  // vacation becomes available (or the id changes), tracking the populated id
  // so we don't clobber the user's subsequent edits.
  const [populatedVacationId, setPopulatedVacationId] = useState<number | null>(null);
  if (isEditing && vacations.length > 0) {
    const editingId = parseInt(id!);
    if (populatedVacationId !== editingId) {
      const vacation = vacations.find((v) => v.id === editingId);
      if (vacation) {
        setPopulatedVacationId(editingId);
        setFormData({
          vacationTypeId: vacation.vacationTypeId.toString(),
          dateFrom: vacation.dateFrom,
          dateTo: vacation.dateTo,
        });
      }
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePeriodChange = (next: VacationPeriodChange) => {
    setFormData((prev) => ({ ...prev, dateFrom: next.dateFrom, dateTo: next.dateTo }));
    setFormErrors((prev) => ({ ...prev, dateFrom: '', dateTo: '' }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.vacationTypeId) errors.vacationTypeId = 'Type is required';
    if (!formData.dateFrom) errors.dateFrom = 'Start date is required';
    if (!formData.dateTo) errors.dateTo = 'End date is required';
    if (formData.dateFrom && formData.dateTo && formData.dateFrom > formData.dateTo) {
      errors.dateTo = 'End date must be on or after start date';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (error) dispatch(clearError());
    if (!validateForm()) return;

    const payload = {
      vacationTypeId: parseInt(formData.vacationTypeId),
      dateFrom: formData.dateFrom,
      dateTo: formData.dateTo,
    };

    try {
      if (isEditing) {
        await dispatch(updateVacationAsync({ id: parseInt(id!), data: payload })).unwrap();
      } else {
        await dispatch(createVacationAsync(payload)).unwrap();
      }
      navigate('/vacations');
    } catch {
      // Error handled by slice
    }
  };

  const handleCancel = () => {
    navigate('/vacations');
  };

  if (loading && isEditing && vacations.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{isEditing ? 'Edit Vacation' : 'Add Vacation'}</h1>
          <p>{isEditing ? 'Update vacation record' : 'Create a new vacation or leave record'}</p>
        </div>
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Vacation Details</h2>

          <div className="form-group">
            <label htmlFor="vacationTypeId">Type *</label>
            <select
              id="vacationTypeId"
              name="vacationTypeId"
              value={formData.vacationTypeId}
              onChange={handleInputChange}
              className={`form-select ${formErrors.vacationTypeId ? 'error' : ''}`}
            >
              {types.length === 0 && <option value="">Loading...</option>}
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {formErrors.vacationTypeId && <span className="error-text">{formErrors.vacationTypeId}</span>}
          </div>

          <StudentVacationPeriodPicker
            dateFrom={formData.dateFrom}
            dateTo={formData.dateTo}
            onChange={handlePeriodChange}
            errors={{ dateFrom: formErrors.dateFrom, dateTo: formErrors.dateTo }}
            disabled={loading}
          />
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleCancel} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-success" disabled={loading}>
            {loading ? 'Saving...' : isEditing ? 'Update Vacation' : 'Create Vacation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VacationForm;
